/**
 * Typed failures from the deterministic social publish.
 *
 * Two kinds, and the difference matters to the merchant:
 *
 *  - {@link SocialSetupError} — the connection is authorized but cannot publish
 *    yet (no Facebook Page, a personal Instagram account, a feed post with no
 *    image). The merchant fixes it themselves, so we show them how.
 *  - {@link SocialPublishError} — anything else, carrying its backend code so the
 *    caller can tell "nothing was posted" apart from "we cannot know".
 *
 * Detection is by `subtype === 'social_setup_required'` and then by `code`, both
 * set by chatbot-api's SocialSetupException and originating in yepairag's
 * core/social/publish.py. NEVER by the wording: the previous implementation
 * substring-matched the English sentences, and since those sentences never
 * reached the browser at all (chatbot-api flattened them into "500 internal
 * processing error"), the match never fired and every social failure — including
 * a merchant who simply forgot an image — latched the channel shut for the
 * session with "an earlier publish never reported back".
 *
 * The merchant-facing copy lives HERE rather than in the backend: it is product
 * wording with a call-to-action, and it should be translatable without a deploy
 * of two other services.
 */
import { isErrorEnvelope, type ErrorEnvelope } from '@lib/services/shopify/scopeError';

export { isErrorEnvelope, type ErrorEnvelope };

export const SOCIAL_SETUP_REQUIRED = 'social_setup_required';

export interface SocialSetupGuidance {
  title: string;
  /** What went wrong, in the merchant's terms. Doubles as the inline error line. */
  body: string;
  /** The off-site fix. Empty when there is nowhere to send them — see image_required. */
  ctaLabel: string;
  ctaUrl: string;
  /** An extra line for when the off-site fix alone may not be enough. */
  followUp?: string;
}

/**
 * Per-code copy. Keyed by yepairag's stable slug, so rewording any of this cannot
 * change which case is detected.
 */
const GUIDANCE: Record<string, SocialSetupGuidance> = {
  no_managed_page: {
    title: 'No Facebook Page found',
    body: "The connected Facebook account doesn't have a Business Page yet, and Facebook only allows publishing to Pages — not personal profiles.",
    ctaLabel: 'Create a Facebook Page',
    ctaUrl: 'https://www.facebook.com/pages/create',
    // An empty Page list also means "owns a Page, granted this connection none",
    // and Facebook gives us no way to tell the two apart.
    followUp:
      'Already have a Page? Reconnect Facebook under Integrations instead, and grant access to it.',
  },
  no_business_account: {
    title: 'Instagram needs a Professional account',
    body: "This Instagram connection is a personal account. Instagram's API only publishes from Business or Creator accounts.",
    ctaLabel: 'Switch to a Professional account',
    ctaUrl: 'https://www.instagram.com/accounts/convert_to_professional_account/',
    // Instagram's publishing permissions are granted at connect time, so a token
    // issued for a personal account may not pick up the new type on its own.
    followUp:
      'If publishing still fails afterwards, disconnect and reconnect Instagram under Integrations so we pick up the new account type.',
  },
  image_required: {
    title: 'Instagram needs an image',
    body: "Instagram feed posts need an image. This draft doesn't have one — add or generate a cover image, then publish again.",
    // No off-site step: the fix is right here in the draft, and a dialog offering
    // nothing to click would be a worse version of the inline error line.
    ctaLabel: '',
    ctaUrl: '',
  },
};

export class SocialSetupError extends Error {
  /** Backend slug, e.g. `no_managed_page`. Everything branches off this. */
  readonly code: string;
  /** `facebook` | `instagram`, lowercased by chatbot-api. */
  readonly platform: string;
  /** Null for a setup code we have no copy for yet — the backend message stands in. */
  readonly guidance: SocialSetupGuidance | null;

  constructor(message: string, code: string, platform: string) {
    const guidance = GUIDANCE[code] ?? null;
    // Our copy wins over the backend sentence, which is the log line and the fallback.
    super(guidance?.body || message);
    this.name = 'SocialSetupError';
    this.code = code;
    this.platform = platform;
    this.guidance = guidance;
    // Required for `instanceof` to hold when targeting ES5-era output.
    Object.setPrototypeOf(this, SocialSetupError.prototype);
  }

  /** Whether there is somewhere to send the merchant — i.e. whether a dialog helps. */
  get hasDialog(): boolean {
    return !!this.guidance?.ctaUrl;
  }
}

/**
 * Backend codes that PROVE nothing reached the platform, so offering a retry cannot
 * double-post. Anything uncoded — a timeout above all — is NOT here: a timeout is
 * indistinguishable from a slow success.
 *
 * `publish_step_failed` is the subtle one: Instagram accepted the media container but
 * never published it, and containers self-expire in 24h, so the grid stayed empty.
 */
const RETRY_SAFE_CODES = new Set([
  'no_managed_page',
  'no_business_account',
  'image_required',
  'publish_step_failed',
  'unsupported_platform',
  // Both fail while working out WHERE to post, before anything is sent.
  'page_lookup_failed',
  'account_lookup_failed',
]);

export class SocialPublishError extends Error {
  /** Backend code when one was carried, else ''. */
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'SocialPublishError';
    this.code = code;
    Object.setPrototypeOf(this, SocialPublishError.prototype);
  }

  get retryable(): boolean {
    return RETRY_SAFE_CODES.has(this.code);
  }
}

/**
 * chatbot-api prefixes the message with the code (`provider_error: …`) for every
 * failure that isn't a setup one, precisely so the code survives the error envelope.
 * At least one underscore is required, so an ordinary sentence starting with a word
 * and a colon is not mistaken for a code.
 */
const CODE_PREFIX = /^([a-z][a-z0-9]*(?:_[a-z0-9]+)+):\s*(.*)$/s;

/** Build a SocialSetupError from an error envelope, or null for any other failure. */
export function socialSetupErrorFromEnvelope(envelope: ErrorEnvelope): SocialSetupError | null {
  if (envelope.subtype !== SOCIAL_SETUP_REQUIRED) return null;
  const payload = (envelope.data ?? {}) as Record<string, unknown>;
  return new SocialSetupError(
    envelope.errorMessage || 'This social connection is not set up to publish yet.',
    typeof payload.code === 'string' ? payload.code : '',
    typeof payload.platform === 'string' ? payload.platform : ''
  );
}

/** Split chatbot-api's `code: message` into a typed error. */
export function socialPublishErrorFromMessage(raw: string): SocialPublishError {
  const matched = CODE_PREFIX.exec(raw);
  return matched
    ? new SocialPublishError(matched[2], matched[1])
    : new SocialPublishError(raw, '');
}

/** Narrow a caught value at a `catch` site. */
export function asSocialSetupError(err: unknown): SocialSetupError | null {
  return err instanceof SocialSetupError ? err : null;
}

/**
 * Whether retrying after `err` is safe — i.e. whether it PROVES nothing was posted.
 * A setup failure always is: the platform refused before anything was written, and
 * the merchant is about to fix it and come straight back.
 */
export function isSocialSafeToRetry(err: unknown): boolean {
  if (err instanceof SocialSetupError) return true;
  return err instanceof SocialPublishError && err.retryable;
}
